
import CartPromoCodeForm from '@/components/CartPromoCodeForm'
import EstimatedDeliveryDate from '@/components/EstimatedDeliveryDate.vue'
import CartVehicles from '@/components/CartVehicles.vue'
import AlertSellInSingleCopy from '@/components/AlertSellInSingleCopy.vue'
import cartMixin from '@/mixins/cartMixin'
import { debounce } from 'debounce'
import { manualLanguageMention } from '@/utils/manualLanguage'
import WarningProductBadge from '@/components/WarningProductBadge.vue'
import userCountryMixin from '@/mixins/userCountryMixin'
import priceCountryMixin from '@/mixins/priceCountryMixin'
import { bulbPairSuggestions } from '@/utils/bulbPairSuggestion'
import { instalmentOffers } from '@/utils/paymentOffers'
import { shippingZoneOf, zonedShippingField } from '@/utils/shippingZone'
import {
  cartLineHeadlightLabel,
  isSharedBulbDuplicate,
  isSharedBulbSpare,
  sharedBulbDuplicateParams,
} from '@/utils/sharedBulbCart'
let fileDownload = require('js-file-download')

const OUT_OF_STOCK_SOLD_OUT = 1
const OUT_OF_STOCK_DISCONTINUED = 4

export default {
  name: 'CartProductList',
  components: {
    CartPromoCodeForm,
    EstimatedDeliveryDate,
    CartVehicles,
    AlertSellInSingleCopy,
    WarningProductBadge,
  },
  mixins: [cartMixin, userCountryMixin, priceCountryMixin],
  props: {
    order: Object,
    read_only: {
      default: null,
      type: Boolean,
    },
    show_manuals: {
      default: false,
      type: Boolean,
    },
    showTotalOrder: {
      default: true,
      type: Boolean,
    },
    showOrderComment: {
      default: false,
      type: Boolean,
    },
    showVehicles: {
      default: false,
      type: Boolean,
    },
    minimize: {
      default: false,
      type: Boolean,
    },
    show_promo_code_form: {
      default: true,
      type: Boolean,
    },
    promo_code_button: {
      default: false,
      type: Boolean,
    },
    viewAsSlider: {
      default: false,
      type: Boolean,
    },
    smallProducts: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      id: '',
      cartProductsWorking: false,
      deletedLineIds: [],
      items: [],
      expandWarning: false,
      fields: [
        {
          key: 'variant.name',
          label: this.$t('components.cartProductList.products'),
          sortable: false,
          tdClass: this.$device.isMobile ? '!tw-w-full' : '!tw-w-[40rem]',
        },
        this.minimize === false && !this.$device.isMobile
          ? {
              key: 'price_ttc',
              label:
                this.$auth.loggedIn &&
                (!this.$store.getters.show_vat || this.$store.getters.is_pro)
                  ? this.$t('components.cartProductList.unitPriceHT')
                  : this.$t('components.cartProductList.unitPrice'),
              tdClass: 'text-sm-center',
              thClass: 'text-sm-center',
              thStyle: 'width:160px;',
              sortable: false,
            }
          : null,
        !this.$device.isMobile
          ? {
              key: 'quantity',
              label: this.$t('components.cartProductList.quantity'),
              thClass: 'text-center',
              thStyle: 'width:120px;',
              sortable: false,
            }
          : undefined,
        !this.$device.isMobile
          ? {
              key: 'total_ttc',
              label: this.$t('components.cartProductList.total'),
              tdClass: 'text-sm-right',
              thClass: 'text-sm-right',
              sortable: false,
            }
          : undefined,
      ],
      has_promo_code: true,
    }
  },

  methods: {
    headlightLabel(order_variant) {
      return cartLineHeadlightLabel(order_variant)
    },

    isSharedBulbSpare(order_variant) {
      return isSharedBulbSpare(order_variant)
    },

    isSharedBulbDuplicate(order_variant) {
      return isSharedBulbDuplicate(order_variant)
    },

    sharedBulbDuplicateLabel(order_variant) {
      const params = sharedBulbDuplicateParams(order_variant)

      return params.vehicle
        ? this.$t('components.cartProductList.sharedBulbDuplicate', params)
        : this.$t(
            'components.cartProductList.sharedBulbDuplicateWithoutVehicle',
            params
          )
    },

    showsVehicleContext(order_variant) {
      if (!order_variant.metadata?.vehicle_name) {
        return false
      }

      return !(this.showVehicles && this.isLineIncompatible(order_variant))
    },

    isLineIncompatible(order_variant) {
      return (
        (order_variant.vehicle_compatibility ||
          this.order.vehicle_compatibility?.[order_variant.id]) ===
        'incompatible'
      )
    },

    isUnavailable(orderVariant) {
      if (this.order?.ordered_at) return false

      const variant = orderVariant?.variant
      return (
        Boolean(variant) &&
        [OUT_OF_STOCK_SOLD_OUT, OUT_OF_STOCK_DISCONTINUED].includes(
          Number(variant.out_of_stock_status_id)
        ) &&
        Number(variant.quantity_available) < Number(orderVariant.quantity)
      )
    },

    maxQuantityFor(variant) {
      const capped = [
        OUT_OF_STOCK_SOLD_OUT,
        OUT_OF_STOCK_DISCONTINUED,
      ].includes(Number(variant?.out_of_stock_status_id))

      if (!capped) {
        return null
      }

      return Math.max(1, Number(variant?.quantity_available) || 0)
    },

    incompatibleVehicles(order_variant) {
      if (!this.showVehicles) {
        return []
      }

      const states = order_variant.vehicle_compatibility_by_vehicle || {}

      return (this.order?.vehicles || [])
        .filter(vehicle => states[vehicle.id] === 'incompatible')
        .map(vehicle => ({
          id: vehicle.id,
          name: [vehicle.brand?.name, vehicle.name].filter(Boolean).join(' '),
        }))
    },

    tryPromoCode() {
      this.has_promo_code = !this.has_promo_code
    },
    saveOrderComment() {
      this.$orderService.saveCustomerInstruction(this.order.id, {
        customer_instructions: this.order.customer_instructions,
      })
    },

    async downloadManual(manual) {
      if (!manual?.id) {
        this.$swalErrorMessage(
          this.$t('components.cartProductList.documentationNotAvailable')
        )
        return
      }

      try {
        const res = await this.$uploadService.download(manual.id)

        if (!res || !res.data) {
          throw new Error('Fichier non disponible')
        }

        const filename =
          manual.label || manual.title
            ? `${manual.label || manual.title}.${manual.extension || 'pdf'}`
            : `Notice.${manual.extension || 'pdf'}`
        fileDownload(res.data, filename)
      } catch (error) {
        console.error('Download error:', error)
        this.$swalErrorMessage(error)
      }
    },

    manualLanguage(manual) {
      return manualLanguageMention(
        { ...manual, isManual: true },
        this.$i18n.locale,
        this.$i18n.locales
      )
    },

    changeQuantity: debounce(function (value, product_id) {
      if (value < 0 || !value) value = 1

      this.cartProductsWorking = true

      this.$orderProductService
        .update(product_id, { quantity: Number(value) })
        .then(data => {
          this.$emit('updateQuantity')
          setTimeout(() => {
            this.cartProductsWorking = false
          }, 400)
        })
        .catch(error => {
          this.$emit('updateQuantity')
          this.$swalErrorMessage(error)
          this.cartProductsWorking = false
        })
    }, 400),

    deleteProduct(product_id) {
      if (this.deletedLineIds.includes(product_id)) return
      this.deletedLineIds.push(product_id)
      this.cartProductsWorking = true
      this.$orderProductService
        .delete(product_id)
        .then(data => {
          this.$emit('updateQuantity')
          setTimeout(() => {
            this.cartProductsWorking = false
          }, 400)
        })
        .catch(error => {
          this.deletedLineIds = this.deletedLineIds.filter(
            id => id !== product_id
          )
          this.$emit('updateQuantity')
          this.$swalErrorMessage(error)
          this.cartProductsWorking = false
        })
    },
    async activeNewsletter() {
      await this.$newsletterSubService
        .enable(this.$auth.user.newsletter_subscription.id)
        .then(({ data, meta, links }) => {
          this.$auth.fetchUser()
          this.$swalCustomSuccess(
            this.$t('components.cartProductList.addedToNewsletterSuccess'),
            this.$t('components.cartProductList.newsletterSubscriptionBenefit')
          )
        })
    },
  },
  computed: {
    pairSuggestions() {
      const lines = this.order?.order_variants || []
      const suggestions = bulbPairSuggestions(lines)
      const reachable = {}

      lines.forEach(line => {
        const suggestion = suggestions[line.id]
        const max = this.maxQuantityFor(line.variant)
        if (suggestion && (max === null || suggestion.quantity <= max)) {
          reachable[line.id] = suggestion
        }
      })

      return reachable
    },
    instalments() {
      return instalmentOffers(
        this.$store.getters.payment_offers,
        this.order.total_ttc
      )
    },
    paypalCreditLineKey() {
      return this.$store.getters.payment_offers.country === 'FR'
        ? 'components.cartProductList.orInMonthlyInstallmentsWithPaypal'
        : 'components.cartProductList.orInMonthlyInstallmentsWithPaypalAbroad'
    },
    rows() {
      return this.items.length
    },
    shippingCountry() {
      return this.order?.shipping_address?.country
    },
    shippingZone() {
      return shippingZoneOf(this.shippingCountry)
    },
    countryRestrictionLabel() {
      return this.$t('components.cartProductList.countryRestrictedLine', {
        country:
          this.order?.country_restriction_country ||
          this.shippingCountry?.name ||
          this.$store.state.vat_country ||
          this.vatCountryIso,
      })
    },
    vatCountryIso() {
      return this.shippingCountry?.iso ?? this.priceCountry
    },
    freeShippingOffer() {
      return this.$store.getters.free_shipping_offer_for(this.vatCountryIso)
    },
    freeShippingThreshold() {
      return this.freeShippingOffer?.amount
    },
    freeShippingLabelKey() {
      if (this.vatCountryIso === 'FR') {
        return 'components.cartProductList.freeFrenchDeliveryBeforeAmount'
      }

      return this.freeShippingOffer?.relay
        ? 'components.cartProductList.freeRelayDeliveryBeforeAmount'
        : 'components.cartProductList.freeDeliveryBeforeAmount'
    },
    shippingMethodName() {
      return (
        zonedShippingField(
          this.order?.shipping_method_range?.shipping_method,
          'name',
          this.shippingZone
        ) || ''
      )
    },
    shippingMethodImage() {
      return zonedShippingField(
        this.order?.shipping_method_range?.shipping_method,
        'image',
        this.shippingZone
      )
    },
  },
}
